import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Track } from 'livekit-client';
import { observer } from 'mobx-react-lite';
import { Typography } from '@web-chat-core/ui-kit';
import { useVideoStatistic } from 'features/VideoStatistic/hooks/useVideoStatistic';
import { VideoInfo } from 'features/VideoStatistic';
import { selectFirstScreenSharingParticipant, selectHasRecorder } from 'state/selectors';
import { getNameFromMetadata } from 'useCase';
import { ScreenAuthorDescription, ScreenShareContainer, ScreenShareVideoRenderer, VideoOverlay } from './view';

interface ScreenShareProps {
    track: Track;
    shareRef?: React.RefObject<HTMLDivElement | null>;
    isAuthorShown?: boolean;
}

export const ScreenShare = observer(({ track, shareRef, isAuthorShown = false }: ScreenShareProps) => {
    const { t } = useTranslation();
    const { onVideoSizeChanged, ...videoInfo } = useVideoStatistic({
        type: 'track',
        entity: track,
    });

    const hasRecorder = selectHasRecorder();

    const { name, isMe } = useMemo(() => {
        const participant = selectFirstScreenSharingParticipant();
        const name = getNameFromMetadata(participant?.metadata);
        const isMe = Boolean(participant?.isLocal);
        return { name, isMe };
    }, [track]);

    const screenShareAuthorText = isMe
        ? t('web_screen_sharing_you_are_sharing')
        : t('web_screen_sharing_participant_is_sharing', { 0: name });

    return (
        <ScreenShareContainer ref={shareRef}>
            <VideoInfo {...videoInfo} />
            <ScreenShareVideoRenderer track={track} isLocal={false} onSizeChanged={onVideoSizeChanged} />
            <VideoOverlay $isMyScreen={isMe} />

            <ScreenAuthorDescription $isMe={isMe} $hidden={!isAuthorShown}>
                <Typography variant="caption4">{hasRecorder ? name : screenShareAuthorText}</Typography>
            </ScreenAuthorDescription>
        </ScreenShareContainer>
    );
});

ScreenShare.displayName = 'ScreenShare';
